import React, { useEffect, useRef, useState } from 'react';

import Icon from 'components/shared/Icon/Icon';

const FloatingButton = React.forwardRef(({ onClick, buttonPosition, setButtonPosition }, ref) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const buttonRef = useRef(null);

  React.useImperativeHandle(ref, () => buttonRef.current);

  const handleStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    const rect = buttonRef.current.getBoundingClientRect();
    const clientX = e.clientX || e.touches?.[0]?.clientX;
    const clientY = e.clientY || e.touches?.[0]?.clientY;
    setDragOffset({
      x: clientX - rect.left,
      y: clientY - rect.top
    });
  };

  const handleMove = (e) => {
    if (!isDragging) return;
    const clientX = e.clientX || e.touches?.[0]?.clientX;
    const clientY = e.clientY || e.touches?.[0]?.clientY;
    if (!clientX || !clientY) return;
    const newLeft = clientX - dragOffset.x;
    const newBottom = window.innerHeight - clientY - dragOffset.y;
    const constrainedLeft = Math.max(0, Math.min(newLeft, window.innerWidth - 72));
    const constrainedBottom = Math.max(0, Math.min(newBottom, window.innerHeight - 56));
    setButtonPosition({ left: constrainedLeft, bottom: constrainedBottom });
  };

  const handleEnd = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleEnd);
      window.addEventListener('touchmove', handleMove, { passive: false });
      window.addEventListener('touchend', handleEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleEnd);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', handleEnd);
    };
  }, [isDragging, dragOffset]);

  return (
    <div
      ref={buttonRef}
      className="fixed z-50 flex items-center"
      style={{ bottom: `${buttonPosition.bottom}px`, left: `${buttonPosition.left}px` }}>
      <div
        onMouseDown={handleStart}
        onTouchStart={handleStart}
        className={`relative bottom-4 left-1 h-4 w-4 cursor-move rounded-sm transition ${
          isDragging ? 'cursor-grabbing' : 'cursor-move'
        }`}
        aria-label="Drag to move button">
        <Icon icon="drag-drop-linear" />
      </div>
      <button
        onClick={onClick}
        className="cursor-pointer rounded-full bg-white p-4 text-white shadow-lg transition hover:bg-primary-50"
        aria-label="Open chat widget">
        <Icon icon="myscribeai-filled" className="relative z-50" />
      </button>
    </div>
  );
});

export default FloatingButton;
